import { geoGraticule10, geoNaturalEarth1, geoPath } from "d3-geo";
import { useEffect, useMemo, useState } from "react";
import { CCN3_TO_ISO2, loadWorld, type World } from "../WorldMap";

/** Box in world units: [x0, y0, x1, y1]. */
export type Box = [number, number, number, number];

export interface CountryShape {
  d: string;
  /** Bounds and centre of the largest polygon — overseas bits must not drag a camera. */
  box: Box;
  c: [number, number];
  /** Every sizeable polygon's centre (≥ 5% of the largest) — for labels near a given point. */
  parts: [number, number][];
}

export interface WorldShapes {
  all: { iso: string; d: string }[];
  by: Map<string, CountryShape>;
  /** 10° graticule as one path. */
  graticule: string;
  /** Project a lon/lat (for countries too small for the 110m map). */
  project: (lng: number, lat: number) => [number, number] | null;
}

const EMPTY: WorldShapes = { all: [], by: new Map(), graticule: "", project: () => null };

/**
 * Natural Earth 110m projected once into a 1000×520 world-unit plane, shared by
 * every map that frames its own camera (Travle's puzzle, Сусіди's neighbourhood).
 */
export function useWorldShapes(): WorldShapes {
  const [world, setWorld] = useState<World | null>(null);
  useEffect(() => {
    void loadWorld().then(setWorld);
  }, []);

  return useMemo(() => {
    if (!world) return EMPTY;
    const projection = geoNaturalEarth1().fitExtent(
      [
        [0, 0],
        [1000, 520],
      ],
      world,
    );
    const gp = geoPath(projection);
    const by = new Map<string, CountryShape>();
    const all = world.features.map((f) => {
      const iso = CCN3_TO_ISO2.get(String(f.id)) ?? "";
      const d = gp(f) ?? "";
      let main: GeoJSON.Geometry = f.geometry;
      let parts: [number, number][] = [gp.centroid(f.geometry) as [number, number]];
      if (f.geometry.type === "MultiPolygon") {
        let best = -1;
        const sized: { c: [number, number]; a: number }[] = [];
        for (const coordinates of f.geometry.coordinates) {
          const poly: GeoJSON.Polygon = { type: "Polygon", coordinates };
          const a = gp.area(poly);
          sized.push({ c: gp.centroid(poly) as [number, number], a });
          if (a > best) {
            best = a;
            main = poly;
          }
        }
        parts = sized.filter((p) => p.a >= best * 0.05).map((p) => p.c);
      }
      const [[x0, y0], [x1, y1]] = gp.bounds(main);
      if (iso) by.set(iso, { d, box: [x0, y0, x1, y1], c: gp.centroid(main) as [number, number], parts });
      return { iso, d };
    });
    return {
      all,
      by,
      graticule: gp(geoGraticule10()) ?? "",
      project: (lng: number, lat: number) => projection([lng, lat]),
    };
  }, [world]);
}
